import { Box } from "@mantine/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { Skeleton, TextSkeleton } from "#components/atoms/Skeleton/Skeleton";
import { createTableColumnHelper } from "#components/organisms/Table/tableHook";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import {
  isBatchSiblingQuoteRequestLineTableRow,
  isLoadingQuoteRequestLineTableRow,
  QuoteRequestLineTableRow,
} from "#modules/quoteRequestLinesV2/domain/buildQuoteRequestLineTableRows";
import { QuoteRequestLineNameSortDirection } from "#modules/quoteRequestLinesV2/domain/sortQuoteRequestLinesByName";
import { BatchQuantityCell } from "#modules/quoteRequestLinesV2/view/organisms/BatchQuantityCell";
import { MarginCell } from "#modules/quoteRequestLinesV2/view/organisms/MarginCell";
import { MaterialCell } from "#modules/quoteRequestLinesV2/view/organisms/MaterialCell";
import { PartCell } from "#modules/quoteRequestLinesV2/view/organisms/PartCell";
import { QuantityCell } from "#modules/quoteRequestLinesV2/view/organisms/QuantityCell";
import { QuantityInAssemblyCell } from "#modules/quoteRequestLinesV2/view/organisms/QuantityInAssemblyCell";
import { SurfaceTreatmentCell } from "#modules/quoteRequestLinesV2/view/organisms/SurfaceTreatmentCell";
import { TotalPriceCell } from "#modules/quoteRequestLinesV2/view/organisms/TotalPriceCell";
import { UnitCostCell } from "#modules/quoteRequestLinesV2/view/organisms/UnitCostCell";
import { UnitPriceCell } from "#modules/quoteRequestLinesV2/view/organisms/UnitPriceCell";
import { ValidatedCell } from "#modules/quoteRequestLinesV2/view/organisms/ValidatedCell";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { PartTypeSchema } from "shared";

export const PRICE_CELL_OVERLAY = colors.bg.elevation.overlay;

const columnHelper = createTableColumnHelper<QuoteRequestLineTableRow>();

const PartCellSkeleton = () => (
  <Flex gap={spacing[4]} align="center">
    <Skeleton variant="rectangle" width={48} height={48} />
    <Flex direction="column" gap={spacing[1]} style={{ flex: 1 }}>
      <TextSkeleton size="small" width={230} />
      <TextSkeleton size="tiny" width={160} />
    </Flex>
  </Flex>
);

const PriceCellSkeleton = () => (
  <Box ta="right">
    <TextSkeleton size="small" width={64} />
  </Box>
);

const ValidatedCellSkeleton = () => (
  <Skeleton variant="rectangle" width={20} height={20} radius={4} />
);

const PartColumnHeader = ({
  sortDirection,
  onSortToggle,
}: {
  sortDirection?: QuoteRequestLineNameSortDirection | null;
  onSortToggle?: () => void;
}) => {
  const { t } = useTranslation();
  const isActive = sortDirection === "asc" || sortDirection === "desc";
  const color = isActive ? colors.icon.primaryDefault : colors.icon.tertiary;

  return (
    <Flex
      align="center"
      gap={spacing[1]}
      onClick={onSortToggle}
      style={{ cursor: onSortToggle ? "pointer" : undefined }}
    >
      <span>{t("quoteRequestV2.column.part")}</span>
      {sortDirection === "asc" && <Icon name="sortDown" size={16} color={color} />}
      {sortDirection === "desc" && <Icon name="sortUp" size={16} color={color} />}
    </Flex>
  );
};

export const useQuoteRequestLinesTableColumns = (
  expandedAssemblyIds: ReadonlySet<string>,
  onToggleAssemblyCollapse: (assemblyId: string) => void,
  sortDirection?: QuoteRequestLineNameSortDirection | null,
  onSortToggle?: () => void,
  onEditPart?: (line: QuoteRequestLineDto) => void,
) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      columnHelper.display({
        id: "part",
        header: () => (
          <PartColumnHeader sortDirection={sortDirection} onSortToggle={onSortToggle} />
        ),
        size: 374,
        meta: { sticky: true, verticalPadding: "0px" },
        cell: ({ row: { original } }) => {
          if (isLoadingQuoteRequestLineTableRow(original)) {
            return <PartCellSkeleton />;
          }
          if (isBatchSiblingQuoteRequestLineTableRow(original)) {
            return null;
          }
          const { line, depth, isAssemblyParent, childrenCount, linePrice, isAssemblyAnalyzing } =
            original;
          return (
            <PartCell
              line={line}
              depth={depth}
              isAssemblyParent={isAssemblyParent}
              childrenCount={childrenCount}
              isAssemblyAnalyzing={isAssemblyAnalyzing}
              isCollapsed={!expandedAssemblyIds.has(line.id)}
              onToggleCollapse={() => onToggleAssemblyCollapse(line.id)}
              unitPrice={linePrice?.partPrice ?? null}
              onEdit={onEditPart ? () => onEditPart(line) : undefined}
            />
          );
        },
      }),

      columnHelper.display({
        header: t("quoteRequestV2.column.quantity"),
        minSize: 145,
        maxSize: 168,
        meta: { verticalPadding: spacing[1.5] },
        cell: ({ row: { original } }) => {
          if (isLoadingQuoteRequestLineTableRow(original)) {
            return <TextSkeleton size="small" width={64} />;
          }
          if (isBatchSiblingQuoteRequestLineTableRow(original)) {
            return <BatchQuantityCell line={original.line} />;
          }
          const { line, depth } = original;
          if (depth > 0) {
            return (
              <QuantityInAssemblyCell quantityInAssembly={line.part.quantityInAssembly ?? 1} />
            );
          }
          return <QuantityCell line={line} />;
        },
      }),

      columnHelper.display({
        header: t("quoteRequestV2.column.material"),
        minSize: 186,
        maxSize: 360,
        meta: { verticalPadding: spacing[1.5] },
        cell: ({ row: { original } }) => {
          if (isLoadingQuoteRequestLineTableRow(original)) {
            return <TextSkeleton size="small" width={100} />;
          }
          if (isBatchSiblingQuoteRequestLineTableRow(original)) {
            return null;
          }
          const { line, assembly } = original;
          if (line.part.type === PartTypeSchema.enum.CONSUMABLE) {
            return null;
          }
          return <MaterialCell line={line} assembly={assembly} />;
        },
      }),

      columnHelper.display({
        header: t("quoteRequestV2.column.treatments"),
        minSize: 194,
        maxSize: 368,
        meta: { optional: true, verticalPadding: spacing[1.5] },
        cell: ({ row: { original } }) => {
          if (isLoadingQuoteRequestLineTableRow(original)) {
            return <TextSkeleton size="small" width={100} />;
          }
          if (isBatchSiblingQuoteRequestLineTableRow(original)) {
            return null;
          }
          const { line } = original;
          if (line.part.type === PartTypeSchema.enum.CONSUMABLE) {
            return null;
          }
          return <SurfaceTreatmentCell line={line} />;
        },
      }),

      columnHelper.display({
        header: t("quoteRequestV2.costing.unitCost"),
        size: 180,
        meta: {
          cellOverlay: PRICE_CELL_OVERLAY,
          align: "right",
          verticalPadding: "0px",
        },
        cell: ({ row: { original } }) => {
          if (isLoadingQuoteRequestLineTableRow(original)) {
            return <PriceCellSkeleton />;
          }
          const { line, linePrice, assemblyPrice, isAnalyzingOrPredicting } = original;
          return (
            <UnitCostCell
              line={line}
              linePrice={linePrice}
              assemblyPrice={assemblyPrice}
              isAnalyzingOrPredicting={isAnalyzingOrPredicting}
            />
          );
        },
      }),

      columnHelper.display({
        header: t("quoteRequestV2.costing.margin"),
        minSize: 77,
        maxSize: 109,
        meta: {
          cellOverlay: PRICE_CELL_OVERLAY,
          align: "right",
          verticalPadding: "0px",
        },
        cell: ({ row: { original } }) => {
          if (isLoadingQuoteRequestLineTableRow(original)) {
            return <PriceCellSkeleton />;
          }
          const { line, linePrice, assemblyPrice, isAnalyzingOrPredicting } = original;
          return (
            <MarginCell
              line={line}
              linePrice={linePrice}
              assemblyPrice={assemblyPrice}
              isAnalyzingOrPredicting={isAnalyzingOrPredicting}
            />
          );
        },
      }),

      columnHelper.display({
        header: t("quoteRequestV2.costing.unitPrice"),
        minSize: 115,
        maxSize: 139,
        meta: {
          cellOverlay: PRICE_CELL_OVERLAY,
          align: "right",
          verticalPadding: "0px",
        },
        cell: ({ row: { original } }) => {
          if (isLoadingQuoteRequestLineTableRow(original)) {
            return <PriceCellSkeleton />;
          }
          const { line, linePrice, assemblyPrice, isAnalyzingOrPredicting } = original;
          return (
            <UnitPriceCell
              line={line}
              linePrice={linePrice}
              assemblyPrice={assemblyPrice}
              isAnalyzingOrPredicting={isAnalyzingOrPredicting}
            />
          );
        },
      }),

      columnHelper.display({
        header: t("quoteRequestV2.costing.totalPrice"),
        size: 191,
        meta: {
          cellOverlay: PRICE_CELL_OVERLAY,
          align: "right",
          verticalPadding: "0px",
        },
        cell: ({ row: { original } }) => {
          if (isLoadingQuoteRequestLineTableRow(original)) {
            return <PriceCellSkeleton />;
          }
          const { linePrice, assemblyPrice, isAssemblyParent, isAnalyzingOrPredicting } = original;
          const depth = isBatchSiblingQuoteRequestLineTableRow(original) ? 0 : original.depth;
          return (
            <TotalPriceCell
              depth={depth}
              isAssemblyParent={isAssemblyParent}
              linePrice={linePrice}
              assemblyPrice={assemblyPrice}
              isAnalyzingOrPredicting={isAnalyzingOrPredicting}
            />
          );
        },
      }),

      columnHelper.display({
        id: "validate",
        size: 66,
        meta: { cellOverlay: PRICE_CELL_OVERLAY, headerIcon: <Icon name="check" size={16} /> },
        cell: ({ row: { original } }) => {
          if (isLoadingQuoteRequestLineTableRow(original)) {
            return <ValidatedCellSkeleton />;
          }
          if (isBatchSiblingQuoteRequestLineTableRow(original)) {
            return null;
          }
          return <ValidatedCell line={original.line} />;
        },
      }),
    ],
    [t, expandedAssemblyIds, onToggleAssemblyCollapse, sortDirection, onSortToggle, onEditPart],
  );
};
